<script lang="ts">
import { defineComponent } from 'vue'
import { PrintQueueItem } from '@/modules/level-two/models'
import BadgePrintProviderMixin from '@/modules/common/components/ui-core/gtr-badge-printer/badge-print-provider.mixin'
import BadgePrintQueueMixin from '@/modules/common/components/ui-core/gtr-badge-printer/badge-print-queue.mixin'
import GtrBadgePreview from '@/modules/common/components/ui-core/gtr-badge-preview/gtr-badge-preview.vue'
import PrinterSelect from '@/modules/common/components/ui-core/gtr-badge-printer/printer-select.vue'
import PrintOrderControl from '@/modules/common/components/ui-core/gtr-badge-printer/print-order-control.vue'
import NotesWarningModal from '@/modules/common/components/ui-core/gtr-badge-printer/notes-warning-modal.vue'

export default defineComponent({
  name: 'GtrBadgePrinter',

  components: { GtrBadgePreview, PrinterSelect, PrintOrderControl, NotesWarningModal },

  mixins: [BadgePrintProviderMixin, BadgePrintQueueMixin],

  // #region Props

  props: {
    printQueue: { required: true, type: Array<PrintQueueItem>, default: [] },
    skipDialog: { required: false, type: Boolean, default: false },
    disabled: { required: false, type: Boolean, default: false },
    showPreview: { required: false, type: Boolean, default: false },
    singlePrint: { required: false, type: Boolean, default: false }
  },

  // #region Data

  data () {
    return {
      showModal: false,
      initializing: false,
      step: 'print' as 'preview' | 'print',
      badgeSvg: '',
      loadingPreview: false
    }
  },

  // #region Computed

  computed: {
    printGuardState (): { notesCheckInFlight: boolean, notesWarningShow: boolean } {
      return { notesCheckInFlight: (this as any).notesCheckInFlight, notesWarningShow: (this as any).notesWarningShow }
    },

    primaryStatBox (): { label: string, value: number, items: PrintQueueItem[], dimmed: boolean, hint: string, tone: string } {
      if (this.jobDone) {
        return { label: 'Remaining', value: this.remainingToPrint.length, items: this.remainingToPrint, dimmed: true, hint: '', tone: 'text-complete' }
      }
      if (this.jobStarted) {
        return { label: 'Remaining', value: this.remainingToPrint.length, items: this.remainingToPrint, dimmed: false, hint: '', tone: 'text-incomplete' }
      }
      return { label: 'Queued to print', value: this.itemsToPrint.length, items: this.itemsToPrint, dimmed: false, hint: '', tone: 'text-incomplete' }
    },

    secondaryStatBox (): { label: string, value: number, items: PrintQueueItem[], dimmed: boolean, hint: string, tone: string } {
      if (this.jobDone) {
        return { label: 'Printed', value: this.printed, items: this.printedItems, dimmed: false, hint: 'All done', tone: 'text-incomplete' }
      }
      if (this.jobStarted) {
        return { label: 'Printed', value: this.printed, items: this.printedItems, dimmed: false, hint: '', tone: 'text-complete' }
      }
      return { label: 'Printed', value: 0, items: [], dimmed: true, hint: 'None yet', tone: 'text-complete' }
    },

    activeDrillBox (): { label: string, items: PrintQueueItem[] } | null {
      if (this.expandedDrill === 'primary') return this.primaryStatBox
      if (this.expandedDrill === 'secondary') return this.secondaryStatBox
      return null
    }
  },

  // #region Watchers

  watch: {
    // the hidden single printer cannot render the row's loading state, so the parent mirrors this payload
    printGuardState (state: { notesCheckInFlight: boolean, notesWarningShow: boolean }) {
      this.$emit('print-guard-change', state)
    },
    jobDone (done: boolean) {
      // Dismiss on full success only; any error leaves the modal open so the failure is not missed
      if (done && this.errors === 0) {
        this.scheduleAutoClose(() => { this.showModal = false })
      }
    }
  },

  // #region Methods

  methods: {
    async openDialog (isSinglePrint?: boolean) {
      if (this.initializing || this.printLoopActive || (this as any).notesCheckBusy) return

      // Strict boolean: a DOM event passed by a bare @click binding must never count as a single print
      const single = typeof isSinglePrint === 'boolean' ? isSinglePrint : this.singlePrint === true
      const item = this.printQueue[0]
      if (item) {
        let decision: 'proceed' | 'cancel'
        ;(this as any).notesConfirming = true // same-tick part of notesCheckBusy, cleared once the decision is known
        try {
          decision = await (this as any).confirmSingleBadgePrint(item, single)
        } finally {
          (this as any).notesConfirming = false
        }
        if (decision === 'cancel') return
      }

      this.resetPrintProgress()
      this.badgeSvg = ''
      this.step = 'print'
      this.showModal = true // acknowledge the click right away; the print service check below can take a while

      if (this.badgesEnabledAndAllowed) {
        this.initializing = true
        try {
          await this.$nextTick() // let the provider component switch (script-x/momentive-print) settle first
          await this.initializePrintService()
        } finally {
          this.initializing = false
        }
      }

      if (!this.showModal) return // the user closed the modal while the print service was being checked

      if (this.skipDialog && this.printServiceEnabled) {
        this.printAll()
      } else if (this.showPreview && this.printQueue.length === 1) {
        this.loadBadgePreview()
      }
    },

    closeModal () {
      // Escape bypasses the hidden close button, so guard here as well as with :closable.
      // A paused job may be closed even while its last item settles, so a hung print service cannot trap the user.
      if (this.printing) return
      this.showModal = false
    },

    async loadBadgePreview () {
      this.loadingPreview = true
      this.badgeSvg = ''
      try {
        const response = await (this as any).fetchAttendeeBadgeSvg(this.printQueue[0].participant_uuid as string)
        this.badgeSvg = response?.svg || ''
      } catch {
        this.badgeSvg = ''
      } finally {
        this.loadingPreview = false
      }
    },

    handleContinueFromPreview () {
      this.step = 'print'
    }
  }
})
</script>

<template>
  <div>
    <component :is="providerComponent" ref="provider" />
    <template v-if="badgesEnabledAndAllowed">
      <slot :attrs="{ disabled: disabled || initializing || printLoopActive, loading: notesCheckInFlight }" :on="{ click: () => openDialog() }">
        <v-btn class="priority-default" large :loading="notesCheckInFlight" :disabled="disabled || initializing || printLoopActive" @click="openDialog()">
          <v-icon>mdi-printer</v-icon>
          Print {{ itemsToPrint.length === 1 ? 'Badge' : itemsToPrint.length + ' Badges' }}
        </v-btn>
      </slot>
    </template>
    <notes-warning-modal :visible="notesWarningShow" :notes="notesWarningContent" @confirm="resolveNotesWarning(true)" @cancel="resolveNotesWarning(false)" />
    <gtr-modal title="Print Badges" :value="showModal" :width="640" :closable="!printing" @close="closeModal">
      <div v-if="initializing" class="result-banner result-progress">
        <v-progress-circular indeterminate size="20" width="2" color="var(--teal-200)" />
        <span>Preparing the print service...</span>
      </div>
      <template v-else-if="printServiceEnabled">
        <div v-if="showPreview && printQueue.length === 1" class="mb-5">
          <p class="preview-section-label">BADGE PREVIEW &mdash; {{ getParticipantName(printQueue[0]).toUpperCase() }}</p>
          <v-progress-circular v-if="loadingPreview" indeterminate color="var(--teal-200)" class="d-block mx-auto my-6" />
          <gtr-badge-preview v-else :badge="badgeSvg" :loading="false" :include-frame="true" />
        </div>
        <div v-if="printing || (pausePrinting && printLoopActive)" class="result-banner result-progress">
          <v-progress-circular indeterminate size="20" width="2" color="var(--teal-200)" />
          <span>{{ printing ? 'Printing in progress' : 'Pausing after the current badge' }}<template v-if="currentParticipantName">: {{ currentParticipantName }}</template>. Please keep this window open.</span>
        </div>
        <div v-if="haltMessage && !printing" class="result-banner result-warning">
          <v-icon color="var(--a2z-accent)">mdi-alert-circle</v-icon>
          <span>{{ haltMessage }} {{ printed }} badge{{ printed === 1 ? '' : 's' }} printed.</span>
        </div>
        <div v-if="jobDone && !haltMessage" :class="['result-banner', errors === 0 ? 'result-success' : 'result-warning']">
          <v-icon :color="errors === 0 ? 'var(--a2z-success)' : 'var(--a2z-accent)'">
            {{ errors === 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          <span v-if="errors === 0">{{ printed }} badge{{ printed === 1 ? '' : 's' }} printed successfully</span>
          <span v-else>{{ printed }} of {{ printed + errors }} badges printed ({{ errors }} error{{ errors === 1 ? '' : 's' }})</span>
        </div>
        <v-row v-show="(printing || pausePrinting) && canDrillDown">
          <v-col cols="12" class="text-h6">
            Now Printing: {{ currentParticipantName }}
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="6">
            <v-card
                elevation="0"
                class="stat-card"
                :class="{ dimmed: primaryStatBox.dimmed, clickable: canDrillDown && primaryStatBox.items.length > 0 }"
                @click="canDrillDown && primaryStatBox.items.length > 0 && toggleDrill('primary')"
            >
              <v-card-text>
                <h6 class="text-uppercase">{{ primaryStatBox.label }}</h6>
                <span :class="primaryStatBox.tone">{{ primaryStatBox.value }}</span>
                <div v-if="canDrillDown" class="stat-hint">
                  <template v-if="primaryStatBox.items.length > 0">
                    <v-icon x-small>mdi-information-outline</v-icon> Click to see list
                  </template>
                  <template v-else>{{ primaryStatBox.hint || 'None yet' }}</template>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card
                elevation="0"
                class="stat-card"
                :class="{ dimmed: secondaryStatBox.dimmed, clickable: canDrillDown && secondaryStatBox.items.length > 0 }"
                @click="canDrillDown && secondaryStatBox.items.length > 0 && toggleDrill('secondary')"
            >
              <v-card-text>
                <h6 class="text-uppercase">{{ secondaryStatBox.label }}</h6>
                <span :class="secondaryStatBox.tone">{{ secondaryStatBox.value }}</span>
                <div v-if="canDrillDown" class="stat-hint">
                  <template v-if="secondaryStatBox.items.length > 0">
                    <v-icon x-small>mdi-information-outline</v-icon> Click to see list
                  </template>
                  <template v-else>{{ secondaryStatBox.hint || 'None yet' }}</template>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-expand-transition>
          <div v-if="activeDrillBox" class="drill-panel">
            <div class="drill-panel-head">
              <span>{{ activeDrillBox.label }}</span>
              <v-btn text x-small @click="expandedDrill = null">Collapse</v-btn>
            </div>
            <div class="drill-list">
              <div v-for="item in activeDrillBox.items" :key="item.participant_uuid" class="drill-item">
                <v-avatar size="32" color="#C4C4C4" class="mr-2">
                  <span class="white--text">{{ getInitials(item) }}</span>
                </v-avatar>
                <span class="drill-name">{{ getParticipantName(item) }}</span>
                <span v-if="item.registration_type" class="drill-type">{{ item.registration_type }}</span>
              </div>
            </div>
          </div>
        </v-expand-transition>
        <v-card elevation="0" color="var(--a2z-primary-bg)" class="my-5" style="border-radius: 8px;">
          <v-container>
            <v-row align="center">
              <v-col cols="3">
                Printer:
              </v-col>
              <v-col>
                <printer-select v-model="selectedPrinter" :items="availablePrinters" :disabled="jobStarted" />
              </v-col>
            </v-row>
            <print-order-control
              v-if="canDrillDown"
              :sort-fields.sync="sortFields"
              :sort-desc.sync="sortDesc"
              :fields="sortableFields"
              :disabled="printing || pausePrinting"
            />
          </v-container>
        </v-card>
        <v-row align="center">
          <v-spacer />
          <v-col v-if="jobDone" cols="auto">
            <v-btn class="priority-focus" @click="showModal = false">Done</v-btn>
          </v-col>
          <v-col v-else-if="printing" cols="auto">
            <v-btn class="priority-danger" @click="pause">Pause Printing</v-btn>
          </v-col>
          <v-col v-else cols="auto">
            <v-btn v-show="!pausePrinting" class="priority-default transparent-btn" @click="showModal = false">Cancel</v-btn>
            <v-btn v-show="!pausePrinting" class="priority-focus" @click="printAll">
              Print {{ itemsToPrint.length }} badge{{ itemsToPrint.length === 1 ? '' : 's' }}
            </v-btn>
            <v-btn v-show="pausePrinting" class="priority-default mr-3" :disabled="printLoopActive" @click="printAll">
              {{ itemsToPrint.length === 1 ? 'Reprint' : `Print All ${itemsToPrint.length}` }}
            </v-btn>
            <v-btn v-show="pausePrinting" class="priority-focus" :disabled="printLoopActive" @click="print">Resume Printing</v-btn>
          </v-col>
        </v-row>
      </template>
      <template v-else-if="!useMps">
        <div class="d-flex align-center">
          <div class="mr-3">
            <v-icon color="var(--a2z-danger)">mdi-printer-off</v-icon>
          </div>
          <div>
            The ScriptX service is not running or is not installed.<br />Please either start the service or <a href="https://support.meadroid.com/Downloads/ScriptXServices/Download" target="_blank" @click="showModal = false">download</a> and install it, and then try again.<br />Alternatively, switch to using the Momentive Print Service.
          </div>
        </div>
      </template>
      <template v-else>
        <div class="d-flex align-center">
          <div class="mr-3">
            <v-icon color="var(--a2z-danger)">mdi-printer-off</v-icon>
          </div>
          <div>
            The Momentive Print Service is not running or is not installed. Please contact support for assistance or switch to using ScriptX.
          </div>
        </div>
      </template>
    </gtr-modal>
  </div>
</template>

<style scoped>
.preview-section-label {
  font-size: 0.72em;
  font-weight: 600;
  letter-spacing: 1.1px;
  opacity: 0.6;
  margin-bottom: 8px;
}

h6 {
  opacity: 0.6;
}

.stat-card {
  border: 1px solid #00000033;
  border-radius: 8px;
}

.stat-card h6 {
  font-size: .95em;
  font-weight: 400;
  letter-spacing: 1.2px;
  margin-bottom: 8px;
}

.stat-card span {
  font-size: 1.5em;
}

.stat-card.dimmed {
  opacity: 0.5;
}

.stat-card.clickable {
  cursor: pointer;
}

.stat-card.clickable:hover {
  border-color: #00000066;
}

.stat-hint {
  font-size: .75em;
  opacity: 0.6;
  margin-top: 4px;
}

.section-rule {
  margin: 32px -24px;
  opacity: 0.6;
}

.priority-default.transparent-btn {
  background: transparent !important;
  border: 1px solid #00000033;
  border-radius: 8px;
  margin-right: 8px;
  color: black !important;
}

.result-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-weight: 500;
}

.result-success {
  background-color: rgba(40, 167, 69, 0.1);
  color: var(--a2z-success);
}

.result-warning {
  background-color: rgba(242, 101, 34, 0.1);
  color: var(--a2z-accent);
}

.result-progress {
  background-color: var(--a2z-primary-bg);
}

.drill-panel {
  border: 1px solid #00000033;
  border-radius: 8px;
  margin-top: 12px;
}

.drill-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-weight: 500;
  border-bottom: 1px solid #00000033;
}

.drill-list {
  max-height: 240px;
  overflow-y: auto;
}

.drill-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #00000014;
}

.drill-item:last-child {
  border-bottom: none;
}

.drill-name {
  flex: 1;
}

.drill-type {
  opacity: 0.6;
  font-size: .9em;
}
</style>
